/**
 * Patient Category Modal Component
 *
 * Component Alpine.js pour la popup modale de catégorisation des patients (privé/social)
 * sur rdv.ardentis.ch
 *
 * @see docs/implementation-guides/TACHE_8_FRONTEND_COMPLETED.md
 */

document.addEventListener('alpine:init', () => {
    Alpine.data('patientCategoryModal', () => ({
        // État du component
        showModal: false,
        currentStep: 1, // 1 = choix privé/social, 2 = sélection organisme
        selectedCategory: '', // 'prive' ou 'social'
        selectedOrganization: null, // objet organisme sélectionné
        organizations: [], // liste des organismes sociaux
        loading: false,
        error: null,

        /**
         * Initialisation du component
         * Appelé automatiquement par Alpine.js au chargement
         */
        async init() {
            await this.checkExistingCategory();
        },

        // Flag pour afficher un petit bandeau de consentement lorsque la catégorie privée
        // est appliquée par défaut mais nécessite la confirmation de l'utilisateur.
        consentBanner: false,

        // Récupère le token CSRF depuis la meta tag si elle existe, sinon retourne null.
        getCsrfToken() {
            const meta = document.querySelector('meta[name="csrf-token"]');
            return meta ? meta.content : null;
        },

        // Wrapper pour envoyer des événements analytics en s'assurant qu'aucune donnée
        // personnelle ne soit envoyée. Seuls 'category' et 'page_name' sont transmis.
        trackEvent(name, payload = {}) {
            if (!window.trackRdvGaEvent) return;
            const sanitized = {
                page_name: payload.page_name || 'rdv_slots_list',
                category: payload.category || this.selectedCategory || null,
            };
            window.trackRdvGaEvent(name, sanitized);
        },

        /**
         * Vérifie si un cookie de catégorie existe déjà.
         * Si aucune catégorie n'est disponible, applique automatiquement le privé par défaut
         * sans afficher la popup.
         */
        async checkExistingCategory() {
            try {
                const response = await fetch('/api/patient-category', {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                if (response.ok) {
                    // Une catégorie existe déjà : conserver l'état, mais ne jamais afficher la popup
                    const data = await response.json();
                    this.selectedCategory = data.category || 'prive';
                    this.selectedOrganization = data.socialOrganization || null;
                    this.currentStep = this.selectedCategory === 'social' ? 2 : 1;
                    this.showModal = false;
                } else if (response.status === 404) {
                    // Pas de cookie : appliquer automatiquement la catégorie privée par défaut
                    await this.applyDefaultPrivateCategory();
                } else {
                    console.error('[Patient Category Modal] Erreur API:', response.status);
                    await this.applyDefaultPrivateCategory();
                }
            } catch (error) {
                console.error('[Patient Category Modal] Erreur réseau:', error);
                // En cas d'erreur, conserver le flux sans popup et basculer sur le privé par défaut
                await this.applyDefaultPrivateCategory();
            }
        },

        /**
         * Applique la catégorie privée par défaut, la trace et la persiste côté API.
         * Utilisé lorsque la catégorie n'est pas encore définie ou après une réinitialisation.
         */
        async applyDefaultPrivateCategory() {
            // Ne pas ouvrir la modal, sélectionner localement "privé" et
            // afficher un petit bandeau demandant confirmation avant de persister.
            this.showModal = false;
            this.currentStep = 1;
            this.selectPrivate();

            // Enregistrer l'événement analytics (seulement category + page_name)
            this.trackEvent('rdv_patient_category_selected', {
                page_name: 'rdv_slots_list',
                category: 'prive',
            });

            // Afficher un bandeau/modal demandant la confirmation de l'utilisateur
            // au lieu de persister automatiquement la catégorie.
            this.consentBanner = true;
        },

        // Appelé lorsque l'utilisateur confirme le bandeau de consentement.
        async confirmDefaultPrivateCategory() {
            this.consentBanner = false;
            await this.submitChoice();
        },

        // Appelé lorsque l'utilisateur refuse/annule le bandeau de consentement.
        cancelDefaultPrivateCategory() {
            this.consentBanner = false;
            // Ne pas persister le choix par défaut; réinitialiser la sélection locale.
            this.selectedCategory = '';
            this.selectedOrganization = null;
            this.currentStep = 1;
        },

        /**
         * Charge la liste des organismes sociaux depuis l'API
         */
        async loadOrganizations() {
            if (this.organizations.length > 0) {
                // Déjà chargés
                return;
            }

            this.loading = true;
            this.error = null;

            try {
                const response = await fetch('/api/patient-category/organizations', {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    // La réponse API retourne les données dans data.data (Laravel Resource Collection)
                    this.organizations = data.data || [];
                    console.log('[Patient Category Modal] Organismes chargés:', this.organizations.length);
                } else {
                    throw new Error('Erreur lors du chargement des organismes');
                }
            } catch (error) {
                console.error('[Patient Category Modal] Erreur chargement organismes:', error);
                this.error = 'Impossible de charger les organismes sociaux. Veuillez réessayer.';
            } finally {
                this.loading = false;
            }
        },

        /**
         * Retourne les organismes groupés par canton et triés alphabétiquement
         * @returns {Array} Tableau d'objets { canton: string, organizations: Array }
         */
        get organizationsByCantons() {
            // Grouper par canton
            const grouped = this.organizations.reduce((acc, org) => {
                const canton = org.canton || 'Sans canton';
                if (!acc[canton]) {
                    acc[canton] = [];
                }
                acc[canton].push(org);
                return acc;
            }, {});

            // Trier les organismes de chaque canton par nom (alphabétique)
            Object.keys(grouped).forEach(canton => {
                grouped[canton].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
            });

            // Convertir en tableau et trier les cantons alphabétiquement
            const result = Object.keys(grouped)
                .sort((a, b) => a.localeCompare(b, 'fr'))
                .map(canton => ({
                    canton,
                    organizations: grouped[canton]
                }));

            return result;
        },

        /**
         * Sélectionne "Patient privé"
         */
        selectPrivate() {
            this.selectedCategory = 'prive';
            this.selectedOrganization = null;
        },

        /**
         * Sélectionne "Patient social" et passe à l'étape 2
         */
        async selectSocial() {
            this.selectedCategory = 'social';
            this.currentStep = 2;
            await this.loadOrganizations();
        },

        /**
         * Sélectionne un organisme social
         */
        selectOrganization(organization) {
            this.selectedOrganization = organization;
        },

        /**
         * Retour à l'étape 1 depuis l'étape 2
         */
        goBackToStep1() {
            this.currentStep = 1;
            this.selectedCategory = '';
            this.selectedOrganization = null;
            this.error = null;
        },

        /**
         * Vérifie si le bouton "Valider" peut être cliqué
         */
        canSubmit() {
            if (this.currentStep === 1) {
                return this.selectedCategory === 'prive';
            } else if (this.currentStep === 2) {
                return this.selectedCategory === 'social' && this.selectedOrganization !== null;
            }
            return false;
        },

        /**
         * Soumet le choix du patient à l'API
         */
        async submitChoice() {
            if (!this.canSubmit()) {
                return;
            }

            this.loading = true;
            this.error = null;

            try {
                const payload = {
                    category: this.selectedCategory,
                };

                // Si patient social, inclure l'organisme et le canton
                if (this.selectedCategory === 'social' && this.selectedOrganization) {
                    payload.social_organization_id = this.selectedOrganization.id;
                    if (this.selectedOrganization.canton) {
                        payload.canton = this.selectedOrganization.canton;
                    }
                }

                // Avant d'appeler l'API, tracer l'événement analytics (sanitised)
                this.trackEvent('rdv_patient_category_selected', {
                    page_name: 'rdv_slots_list',
                    category: this.selectedCategory,
                });

                const headers = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                };

                const csrfToken = this.getCsrfToken();
                if (csrfToken) {
                    headers['X-CSRF-TOKEN'] = csrfToken;
                } else {
                    console.warn('[Patient Category Modal] CSRF meta tag manquante, en-tête X-CSRF-TOKEN non ajouté');
                }

                const response = await fetch('/api/patient-category', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(payload),
                });

                if (response.ok) {
                    // Succès : fermer la popup
                    this.showModal = false;
                    this.error = null;
                    setTimeout(() => {
                        // Recharger la page pour appliquer le filtrage des cliniques
                        window.location.reload();
                    }, 300);
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Erreur lors de l\'enregistrement');
                }
            } catch (error) {
                console.error('[Patient Category Modal] Erreur soumission:', error);
                this.error = error.message || 'Une erreur est survenue. Veuillez réessayer.';
            } finally {
                this.loading = false;
            }
        },

        /**
         * Réinitialise le choix du patient (supprime le cookie)
         * Appelé depuis le lien "Modifier mon statut" dans le footer
         */
        async resetChoice() {
            this.loading = true;
            this.error = null;

            try {
                const headers = {
                    'Accept': 'application/json',
                };
                const csrfToken = this.getCsrfToken();
                if (csrfToken) {
                    headers['X-CSRF-TOKEN'] = csrfToken;
                } else {
                    console.warn('[Patient Category Modal] CSRF meta tag manquante, en-tête X-CSRF-TOKEN non ajouté pour DELETE');
                }

                const response = await fetch('/api/patient-category', {
                    method: 'DELETE',
                    headers,
                });

                if (response.ok) {
                    // Réinitialiser l'état et conserver la catégorie privée par défaut sans popup
                    this.currentStep = 1;
                    this.selectedCategory = '';
                    this.selectedOrganization = null;
                    await this.applyDefaultPrivateCategory();
                } else {
                    throw new Error('Erreur lors de la suppression');
                }
            } catch (error) {
                console.error('[Patient Category Modal] Erreur reset:', error);
                this.error = 'Impossible de réinitialiser votre choix. Veuillez réessayer.';
            } finally {
                this.loading = false;
            }
        },

        /**
         * Empêche la fermeture de la popup avec Échap
         */
        preventClose(event) {
            event.stopPropagation();
        },
    }));
});


